'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronUp } from 'lucide-react';

const SCROLL_SHOW_THRESHOLD_PX = 300;

export const BlogScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);
  const rafRef = useRef<number | null>(null);

  const syncVisibility = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      const next = window.scrollY >= SCROLL_SHOW_THRESHOLD_PX;
      setVisible((prev) => (prev === next ? prev : next));
    });
  }, []);

  useEffect(() => {
    syncVisibility();
    window.addEventListener('scroll', syncVisibility, { passive: true });
    return () => {
      window.removeEventListener('scroll', syncVisibility);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [syncVisibility]);

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <button
      type='button'
      onClick={scrollToTop}
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      aria-label='Back to top'
      className={`fixed z-20 flex h-12 w-12 items-center justify-center rounded-full border border-border/60 bg-background/95 text-foreground shadow-lg backdrop-blur-sm transition-[opacity,transform] duration-200 hover:border-accent/50 hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
        visible
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-1 opacity-0'
      }`}
      style={{
        bottom: 'max(1.5rem, env(safe-area-inset-bottom, 0px))',
        right: 'max(1.5rem, env(safe-area-inset-right, 0px))',
      }}
    >
      <ChevronUp className='h-6 w-6' aria-hidden />
    </button>
  );
};
